import type SearchPrediction from 'data/search-predictions';

/** Preposition immediately before a place name, stripped along with it. */
const TRAILING_PREPOSITION = /\s+(?:in|near|around|at|within|close to)\s*$/i;

/**
 * How a chosen place reads in the query and on its chip.
 *
 * Go-Search describes a place as "Toronto, ON", but the province is already
 * implied by the choice the user just made, so only the name goes back into the
 * sentence they are writing.
 */
export function placeLabel(prediction: SearchPrediction): string {
  return (prediction.description.split(',')[0] ?? prediction.description).trim();
}

/**
 * Completes the fragment in place, leaving the rest of the sentence untouched.
 *
 * Matched from the end because the fragment was taken from the end: an earlier
 * identical word ("condo in a condo bui") is not the one being completed.
 */
export function completePlaceFragment(query: string, fragment: string, label: string): string {
  const start = query.lastIndexOf(fragment);
  if (start === -1) {
    return query;
  }
  return `${query.slice(0, start)}${label}${query.slice(start + fragment.length)}`;
}

/**
 * Takes a place back out of the query, along with the preposition that
 * introduced it — "condos in Toronto" without its place is "condos", not
 * "condos in", which would immediately be read as a new place being typed.
 */
export function removePlaceFromQuery(query: string, label: string): string {
  const start = query.toLowerCase().lastIndexOf(label.toLowerCase());
  if (start === -1) {
    return query;
  }
  const before = query.slice(0, start).replace(TRAILING_PREPOSITION, '');
  const after = query.slice(start + label.length);
  return `${before}${after}`.replace(/\s{2,}/g, ' ').trimEnd();
}

/** True when the query still names the place that was chosen. */
export function queryNamesPlace(query: string, label: string): boolean {
  return query.toLowerCase().includes(label.toLowerCase());
}
